import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertTriangle, Filter, Search } from 'lucide-react';
import Button from '../../components/ui/Button';

import Select from '../../components/ui/Select';
import ResponseReviewCard from './components/ResponseReviewCard';

import BulkApprovalPanel from './components/BulkApprovalPanel';
import { automatedResponseService } from '../../services/automatedResponseService';

const AIResponseReviewCenter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingResponses, setPendingResponses] = useState([]);
  const [selectedResponses, setSelectedResponses] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBulkPanel, setShowBulkPanel] = useState(false);

  useEffect(() => {
    loadPendingResponses();
  }, [filterStatus, filterPlatform, filterUrgency]);

  const loadPendingResponses = async () => {
    try {
      setLoading(true);
      setError('');
      
      const responses = await automatedResponseService?.getApprovals({
        status: filterStatus === 'all' ? null : filterStatus,
        platform: filterPlatform === 'all' ? null : filterPlatform
      });

      // Apply urgency filter
      let filtered = responses;
      if (filterUrgency !== 'all') {
        filtered = responses?.filter(r => calculateUrgency(r) === filterUrgency);
      }

      // Apply search filter
      if (searchQuery?.trim()) {
        filtered = filtered?.filter(r => 
          r?.comment?.commentText?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          r?.suggestedResponse?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          r?.comment?.commenterName?.toLowerCase()?.includes(searchQuery?.toLowerCase())
        );
      }

      setPendingResponses(filtered);
    } catch (err) {
      console.error('Error loading pending responses:', err);
      setError(err?.message || 'Failed to load pending responses');
    } finally {
      setLoading(false);
    }
  };

  const calculateUrgency = (response) => {
    const safetyScore = response?.safetyScore || 100;
    const hasFlags = response?.safetyFlags?.length > 0;
    const sentiment = response?.comment?.sentiment;
    
    if (hasFlags || safetyScore < 60 || sentiment === 'negative') return 'high';
    if (safetyScore < 80 || sentiment === 'mixed') return 'medium';
    return 'low';
  };

  const handleApprove = async (approvalId, finalResponse, autoPost = false) => {
    try {
      await automatedResponseService?.approveResponse(approvalId, finalResponse, autoPost);
      await loadPendingResponses();
      setSelectedResponses(selectedResponses?.filter(id => id !== approvalId));
    } catch (err) {
      console.error('Error approving response:', err);
      setError(err?.message || 'Failed to approve response');
    }
  };

  const handleReject = async (approvalId, reason) => {
    try {
      await automatedResponseService?.rejectResponse(approvalId, reason);
      await loadPendingResponses();
      setSelectedResponses(selectedResponses?.filter(id => id !== approvalId));
    } catch (err) {
      console.error('Error rejecting response:', err);
      setError(err?.message || 'Failed to reject response');
    }
  };

  const handleEdit = async (approvalId, updatedResponse) => {
    try {
      await automatedResponseService?.updateApproval(approvalId, { suggestedResponse: updatedResponse });
      await loadPendingResponses();
    } catch (err) {
      console.error('Error updating response:', err);
      setError(err?.message || 'Failed to update response');
    }
  };

  const handleSelectResponse = (approvalId) => {
    setSelectedResponses(prev => 
      prev?.includes(approvalId) 
        ? prev?.filter(id => id !== approvalId)
        : [...prev, approvalId]
    );
  };

  const handleSelectAll = () => {
    if (selectedResponses?.length === pendingResponses?.length) {
      setSelectedResponses([]);
    } else {
      setSelectedResponses(pendingResponses?.map(r => r?.id));
    }
  };

  const handleBulkApprove = async (autoPost = false) => {
    try {
      await Promise.all(
        selectedResponses?.map(id => {
          const response = pendingResponses?.find(r => r?.id === id);
          return automatedResponseService?.approveResponse(id, response?.suggestedResponse, autoPost);
        })
      );
      setSelectedResponses([]);
      setShowBulkPanel(false);
      await loadPendingResponses();
    } catch (err) {
      console.error('Error bulk approving:', err);
      setError(err?.message || 'Failed to bulk approve responses');
    }
  };

  const handleBulkReject = async (reason) => {
    try {
      await Promise.all(
        selectedResponses?.map(id => automatedResponseService?.rejectResponse(id, reason))
      );
      setSelectedResponses([]);
      setShowBulkPanel(false);
      await loadPendingResponses();
    } catch (err) {
      console.error('Error bulk rejecting:', err);
      setError(err?.message || 'Failed to bulk reject responses');
    }
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const platformOptions = [
    { value: 'all', label: 'All Platforms' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'linkedin', label: 'LinkedIn' }
  ];

  const urgencyOptions = [
    { value: 'all', label: 'All Urgency Levels' },
    { value: 'high', label: 'High Urgency' },
    { value: 'medium', label: 'Medium Urgency' },
    { value: 'low', label: 'Low Urgency' }
  ];

  const getMetrics = () => {
    const total = pendingResponses?.length || 0;
    const pending = pendingResponses?.filter(r => r?.status === 'pending')?.length || 0;
    const highUrgency = pendingResponses?.filter(r => calculateUrgency(r) === 'high')?.length || 0;
    const avgSafetyScore = total > 0 
      ? Math.round(pendingResponses?.reduce((sum, r) => sum + (r?.safetyScore || 0), 0) / total)
      : 0;

    return { total, pending, highUrgency, avgSafetyScore };
  };

  const metrics = getMetrics();

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Response Review Center</h1>
          <p className="text-gray-600">Review, customize, and approve AI-generated responses before posting</p>
        </div>

        {/* Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Responses</p>
                <p className="text-2xl font-bold text-gray-900">{metrics?.total}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-600">{metrics?.pending}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">High Urgency</p>
                <p className="text-2xl font-bold text-red-600">{metrics?.highUrgency}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg Safety Score</p>
                <p className="text-2xl font-bold text-green-600">{metrics?.avgSafetyScore}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search responses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
            />

            <Select
              options={platformOptions}
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e?.target?.value)}
            />

            <Select
              options={urgencyOptions}
              value={filterUrgency}
              onChange={(e) => setFilterUrgency(e?.target?.value)}
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedResponses?.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-blue-900">
                {selectedResponses?.length} response{selectedResponses?.length > 1 ? 's' : ''} selected
              </p>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowBulkPanel(true)}
                  size="sm"
                  variant="default"
                >
                  Bulk Actions
                </Button>
                <Button
                  onClick={() => setSelectedResponses([])}
                  size="sm"
                  variant="ghost"
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Response Review Queue */}
        {!loading && pendingResponses?.length === 0 && (
          <div className="bg-white rounded-lg p-12 text-center shadow-sm border border-gray-200">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600 mb-6">No pending responses to review at the moment.</p>
            <Button onClick={() => navigate('/comment-automation')}>
              Go to Comment Automation
            </Button>
          </div>
        )}

        {!loading && pendingResponses?.length > 0 && (
          <div className="space-y-4">
            {/* Select All */}
            <div className="flex items-center gap-2 px-2">
              <input
                type="checkbox"
                checked={selectedResponses?.length === pendingResponses?.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="text-sm text-gray-700 font-medium">
                Select All ({pendingResponses?.length})
              </label>
            </div>

            {/* Response Cards */}
            {pendingResponses?.map((response) => (
              <ResponseReviewCard
                key={response?.id}
                response={response}
                urgency={calculateUrgency(response)}
                isSelected={selectedResponses?.includes(response?.id)}
                onSelect={() => handleSelectResponse(response?.id)}
                onApprove={handleApprove}
                onReject={handleReject}
                onEdit={handleEdit}
              />
            ))}
          </div>
        )}

        {/* Bulk Approval Panel Modal */}
        {showBulkPanel && (
          <BulkApprovalPanel
            selectedCount={selectedResponses?.length}
            onApprove={handleBulkApprove}
            onReject={handleBulkReject}
            onClose={() => setShowBulkPanel(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AIResponseReviewCenter;